import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Drawer,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Divider,
  Table,
} from 'antd';

import { useToasts } from 'core/hooks';
import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  usePaymentCalendarSettingsQuery,
  useSetCashBalanceMutation,
  useSetExchangeRateMutation,
  useSetPaymentCalendarSettingsMutation,
} from 'core';

const CURRENCIES = ['EUR', 'USD', 'BYN', 'RUB'];
const DATE_FMT = 'YYYY-MM-DD';

interface IProps {
  open: boolean;
  onClose: () => void;
  /** Re-fetch the calendar after a config change affects the forecast. */
  onChanged: () => void;
}

export const SettingsDrawer = memo<IProps>(({ open, onClose, onChanged }) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const { data, refetch } = usePaymentCalendarSettingsQuery({
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  const [setBalance, { loading: savingBalance }] = useSetCashBalanceMutation();
  const [setRate, { loading: savingRate }] = useSetExchangeRateMutation();
  const [
    setKnobs,
    { loading: savingKnobs },
  ] = useSetPaymentCalendarSettingsMutation();

  const [balanceForm] = Form.useForm();
  const [rateForm] = Form.useForm();
  const [knobsForm] = Form.useForm();

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) =>
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.PaymentCalendar,
      }),
    [addToast],
  );

  const afterSave = useCallback(
    (ok: boolean, errors?: (string | null)[] | null) => {
      if (ok) {
        notify(ToastTypes.success, t('pages.paymentCalendar.toasts.saved'));
        refetch();
        onChanged();
      } else {
        notify(
          ToastTypes.error,
          t('pages.paymentCalendar.toasts.saveFailed'),
          (errors || []).filter(Boolean).join('\n') || undefined,
        );
      }
    },
    [notify, refetch, onChanged, t],
  );

  const onSaveBalance = useCallback(async () => {
    const v = await balanceForm.validateFields();
    const res = await setBalance({
      variables: {
        amountPln: v.amountPln,
        asOfDate: (v.asOfDate as moment.Moment).format(DATE_FMT),
      },
    });
    const p = res.data?.setCashBalance;
    afterSave(!!p?.success, p?.errors);
  }, [balanceForm, setBalance, afterSave]);

  const onSaveRate = useCallback(async () => {
    const v = await rateForm.validateFields();
    const res = await setRate({
      variables: {
        currency: v.currency,
        rateToPln: v.rateToPln,
        effectiveFrom: (v.effectiveFrom as moment.Moment).format(DATE_FMT),
      },
    });
    const p = res.data?.setExchangeRate;
    afterSave(!!p?.success, p?.errors);
  }, [rateForm, setRate, afterSave]);

  const onSaveKnobs = useCallback(async () => {
    const v = await knobsForm.validateFields();
    const res = await setKnobs({
      variables: {
        teacherPayoutDay: v.teacherPayoutDay,
        incomeOffsetDays: v.incomeOffsetDays,
      },
    });
    const p = res.data?.setPaymentCalendarSettings;
    afterSave(!!p?.success, p?.errors);
  }, [knobsForm, setKnobs, afterSave]);

  const settings = data?.paymentCalendarSettings;
  const rates = data?.exchangeRates || [];
  // cashBalances comes back sorted by -as_of_date, so [0] is the latest.
  const latestBalance = data?.cashBalances?.[0];

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      width={420}
      title={t('pages.paymentCalendar.settings.title')}
    >
      <Divider orientation="left">
        {t('pages.paymentCalendar.settings.openingBalance')}
      </Divider>
      <Form
        form={balanceForm}
        layout="vertical"
        key={latestBalance?.pk ?? 'b'}
        initialValues={{
          asOfDate: latestBalance?.asOfDate
            ? moment(latestBalance.asOfDate)
            : moment().startOf('month'),
          amountPln: latestBalance?.amountPln ?? undefined,
        }}
      >
        <Form.Item
          name="asOfDate"
          label={t('pages.paymentCalendar.settings.asOfDate')}
          rules={[{ required: true }]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="amountPln"
          label={t('pages.paymentCalendar.settings.amountPln')}
          rules={[{ required: true }]}
        >
          <InputNumber step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Button type="primary" loading={savingBalance} onClick={onSaveBalance}>
          {t('pages.paymentCalendar.modal.save')}
        </Button>
      </Form>

      <Divider orientation="left">
        {t('pages.paymentCalendar.settings.knobs')}
      </Divider>
      <Form
        form={knobsForm}
        layout="vertical"
        key={settings?.teacherPayoutDay ?? 'k'}
        initialValues={{
          teacherPayoutDay: settings?.teacherPayoutDay ?? 20,
          incomeOffsetDays: settings?.incomeOffsetDays ?? 5,
        }}
      >
        <Form.Item
          name="teacherPayoutDay"
          label={t('pages.paymentCalendar.settings.payoutDay')}
        >
          <InputNumber min={1} max={31} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="incomeOffsetDays"
          label={t('pages.paymentCalendar.settings.incomeOffset')}
        >
          <InputNumber min={0} max={60} style={{ width: '100%' }} />
        </Form.Item>
        <Button type="primary" loading={savingKnobs} onClick={onSaveKnobs}>
          {t('pages.paymentCalendar.modal.save')}
        </Button>
      </Form>

      <Divider orientation="left">
        {t('pages.paymentCalendar.settings.exchangeRates')}
      </Divider>
      <Form form={rateForm} layout="inline" style={{ marginBottom: 12 }}>
        <Form.Item
          name="currency"
          initialValue="EUR"
          rules={[{ required: true }]}
        >
          <Select style={{ width: 90 }}>
            {CURRENCIES.map((c) => (
              <Select.Option key={c} value={c}>
                {c}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="rateToPln" rules={[{ required: true }]}>
          <InputNumber
            step={0.0001}
            placeholder="→ PLN"
            style={{ width: 100 }}
          />
        </Form.Item>
        <Form.Item
          name="effectiveFrom"
          initialValue={moment()}
          rules={[{ required: true }]}
        >
          <DatePicker format="DD.MM.YYYY" style={{ width: 130 }} />
        </Form.Item>
        <Button type="primary" loading={savingRate} onClick={onSaveRate}>
          +
        </Button>
      </Form>
      <Table
        size="small"
        rowKey="pk"
        pagination={false}
        dataSource={rates as Array<Record<string, unknown>>}
        columns={[
          {
            title: t('pages.paymentCalendar.settings.currency'),
            dataIndex: 'currency',
          },
          {
            title: t('pages.paymentCalendar.settings.rate'),
            dataIndex: 'rateToPln',
          },
          {
            title: t('pages.paymentCalendar.settings.from'),
            dataIndex: 'effectiveFrom',
          },
        ]}
      />
    </Drawer>
  );
});
